// Resilient restricted catalog loader.
// Strategy:
//   1) In-memory cache (instant page transitions)
//   2) sessionStorage cache (survives reloads in the same tab)
//   3) Network: GET /api/restricted/products (single endpoint, requires JWT)
//   4) Retry up to 3 times with exponential backoff
//   5) Hardcoded fallback so products ALWAYS render even if the network
//      is unreachable, the JWT is expired, or CORS misbehaves.
//
// Never fetch a single product by slug — always pull the full list (which
// already works in production) and find the slug client-side. This keeps
// us on one endpoint and one cache key.

import { fetchRestrictedProducts, getRestrictedToken } from "@/lib/api";

const CACHE_KEY = "helixora_restricted_catalog_v1";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let memCache = null;
let inflight = null;

// Last-resort hardcoded snapshot of the 3 restricted SKUs so the page
// NEVER renders empty. Prices/stock can drift from server but at least
// the customer sees the catalog. The shop's add-to-cart still works.
const FALLBACK_PRODUCTS = [
    {
        id: "r-test-cyp-250", slug: "test-cyp-250", name: "Testosterone Cypionate",
        dosage: "250mg/mL · 10mL", category: "Restricted", price_usd: 79.00, in_stock: true,
        image: "https://customer-assets.emergentagent.com/job_sterile-science/artifacts/xakatqmd_image.png",
        short: "Long-ester injectable androgen. 250mg per millilitre in oil, 10mL multi-dose vial.",
        what_we_know: "Testosterone Cypionate is the cyclopentylpropionate ester of endogenous testosterone, dissolved in MCT or grapeseed carrier oil for slow-release intramuscular research administration. The cypionate ester extends the elimination half-life of free testosterone to approximately eight days, with peak plasma concentrations reached 24 to 48 hours after injection. First synthesised in the 1950s, it remains one of the most extensively characterised androgens in pharmacokinetic literature.",
        what_it_does: "In research models, testosterone cypionate elevates serum total and free testosterone, drives androgen-receptor (AR) mediated transcription of myogenic and erythropoietic genes, increases red blood cell mass and haematocrit, suppresses pituitary LH and FSH via negative feedback on the HPG axis, and modestly aromatises to estradiol via CYP19. Effects on muscle protein synthesis, nitrogen retention, and behavioural endpoints are widely documented in both rodent and primate studies.",
        dosage_info: "Typical research protocol: 250 to 500mg per week, intramuscular, single or split twice-weekly injection. Steady-state plasma concentrations are reached after 4 to 5 half-lives (approximately 5 weeks). Cycle length in established protocols ranges 8 to 16 weeks. PCT (post-cycle therapy with clomiphene or tamoxifen) is required to restore endogenous LH/FSH after suppression.",
        lasts: "A 10mL × 250mg/mL vial contains 2,500mg total. At 250mg/week this supports approximately 10 weeks of research, or 5 weeks at 500mg/week.",
        coa_url: "https://janoshik.com/", restricted: true, region: "NA",
        molecule: { source: "pubchem", id: "6402", label: "Testosterone Cypionate" },
        ester: "Cypionate (cyclopentylpropionate)", half_life: "≈ 8 days",
        structure_class: "Testosterone ester · C8 alkyl chain",
        aromatization: "Moderate · aromatises to estradiol via CYP19",
        hepatotoxic: "No · 17β-ester, parenteral, bypasses first-pass liver metabolism",
        ar_affinity: "High (parent testosterone reference, relative AR binding ≈ 100)",
        common_stacks: "Solo TRT-modelled studies, Test/Deca, Test/Tren, Test/Mast",
        pct_required: "Yes · clomiphene 25mg/day + tamoxifen 20mg/day for 4 weeks post-cycle is the most commonly cited research protocol",
        side_effect_profile: "Estrogen-mediated effects (water retention, gynecomastia risk) manageable with AI. Hematocrit > 54% is a frequent finding requiring monitoring. Lipid panel: HDL suppression of 15 to 30%.",
        mg_per_ml: 250, vial_ml: 10, chemical_formula: "C27H40O3", molar_mass_g_mol: 412.61,
    },
    {
        id: "r-test-enan-300", slug: "test-enan-300", name: "Testosterone Enanthate",
        dosage: "300mg/mL · 10mL", category: "Restricted", price_usd: 72.00, in_stock: true,
        image: "https://customer-assets.emergentagent.com/job_sterile-science/artifacts/xakatqmd_image.png",
        short: "Mid-long ester injectable androgen. 300mg per millilitre in oil, 10mL multi-dose vial.",
        what_we_know: "Testosterone Enanthate is the heptanoate (enanthoic acid) ester of testosterone, the most widely studied long-acting androgen in clinical and pre-clinical research. The enanthate ester confers a 7-day terminal half-life with peak release at days 2 to 3 post-injection. Synthesised by Watson and re-formulated by Schering since the late 1950s, enanthate is the reference compound for most modern androgen pharmacokinetic and androgen-replacement studies.",
        what_it_does: "Enanthate produces the same AR-mediated downstream effects as cypionate (myogenesis, erythropoiesis, HPG-axis suppression, modest aromatisation) with a slightly shorter ester. Research applications include sustained androgen-receptor activation studies, hematocrit and EPO modelling, lipid-panel response (HDL suppression), and downstream LH/FSH feedback in HPG-axis recovery research.",
        dosage_info: "Typical research protocol: 300 to 600mg per week, intramuscular, split twice weekly for plasma stability. Steady state reached after 35 days. Cycle length 8 to 16 weeks. PCT required to restore HPG-axis function.",
        lasts: "A 10mL × 300mg/mL vial contains 3,000mg total. At 300mg/week this supports approximately 10 weeks of research; 5 weeks at 600mg/week.",
        coa_url: "https://janoshik.com/", restricted: true, region: "NA",
        molecule: { source: "pubchem", id: "9416", label: "Testosterone Enanthate" },
        ester: "Enanthate (heptanoate, C7)", half_life: "≈ 7 days",
        structure_class: "Testosterone ester · C7 alkyl chain",
        aromatization: "Moderate · aromatises to estradiol via CYP19",
        hepatotoxic: "No · 17β-ester, parenteral, bypasses first-pass liver metabolism",
        ar_affinity: "High (relative AR binding ≈ 100)",
        common_stacks: "Solo TRT-model, Test/Deca, Test/Tren-E, Test/EQ",
        pct_required: "Yes · standard clomiphene + tamoxifen 4-week PCT after final injection (wait 2 weeks for ester clearance)",
        side_effect_profile: "Identical profile to cypionate. Slightly faster ester clearance shortens PCT wait window.",
        mg_per_ml: 300, vial_ml: 10, chemical_formula: "C26H40O3", molar_mass_g_mol: 400.59,
    },
    {
        id: "r-tren-ace-100", slug: "tren-ace-100", name: "Trenbolone Acetate",
        dosage: "100mg/mL · 10mL", category: "Restricted", price_usd: 89.00, in_stock: true,
        image: "https://customer-assets.emergentagent.com/job_sterile-science/artifacts/xakatqmd_image.png",
        short: "Short-ester 19-nor androgen. 100mg per millilitre in oil, 10mL multi-dose vial.",
        what_we_know: "Trenbolone Acetate is the acetate ester of 17β-hydroxyestra-4,9,11-trien-3-one, a synthetic 19-nortestosterone derivative with two additional double bonds (Δ9 and Δ11) in the steroid B and C rings. Originally developed by Roussel-UCLAF for veterinary livestock growth research (Finaplix), it remains the reference compound for studies of high-AR-affinity, non-aromatisable androgens. The acetate ester yields a short 1 to 2 day half-life requiring frequent administration.",
        what_it_does: "Trenbolone exhibits the highest AR binding affinity of any commonly studied androgen (≈ 500% of testosterone) and does NOT aromatise to estrogen, but does bind progesterone receptors. Research applications include studies of pure AR-driven myogenesis, nutrient partitioning, lipolysis, anti-catabolic effects (anti-glucocorticoid via cortisol-receptor displacement), and IGF-1 axis modulation. Notably increases nitrogen retention and feed efficiency in livestock models.",
        dosage_info: "Typical research protocol: 50 to 100mg every other day or 75mg every other day, intramuscular. Some advanced protocols use 50mg daily. Short ester demands frequent injection for stable plasma levels. Cycle length typically 6 to 10 weeks (shorter than testosterone esters due to side-effect burden in research models).",
        lasts: "A 10mL × 100mg/mL vial contains 1,000mg total. At 75mg EOD this supports approximately 4 weeks of research; 2 to 3 weeks at higher doses.",
        coa_url: "https://janoshik.com/", restricted: true, region: "NA",
        molecule: { source: "pubchem", id: "9588", label: "Trenbolone Acetate" },
        ester: "Acetate (C2)", half_life: "≈ 1 to 2 days",
        structure_class: "19-nor-Δ9,11 androgen (trienolone) ester",
        aromatization: "None · does NOT convert to estrogen (CYP19-resistant)",
        hepatotoxic: "No (parenteral) · but elevated renal/cardiac stress reported in long-term livestock studies",
        ar_affinity: "Very high (relative AR binding ≈ 500, ≈ 5× testosterone)",
        common_stacks: "Test/Tren is the canonical research stack (testosterone base provides estrogen for libido/lipids since tren provides none)",
        pct_required: "Yes · clomiphene + tamoxifen + extended HPG-axis monitoring. Progesterone-receptor binding adds prolactin considerations (cabergoline 0.5mg/week in advanced protocols)",
        side_effect_profile: "Highest-burden compound in this catalog. Documented in research literature: increased aggression and anxiety endpoints, sleep disruption, night sweats, cardiovascular stress (LV hypertrophy in livestock), renal stress, progesterone-mediated effects (rare gynecomastia despite no aromatisation), suppressed cardio capacity.",
        mg_per_ml: 100, vial_ml: 10, chemical_formula: "C20H24O3", molar_mass_g_mol: 312.41,
    },
];

function readSession() {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.products || !Array.isArray(parsed.products) || !parsed.ts) return null;
        if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
        return parsed.products;
    } catch { return null; }
}

function writeSession(products) {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), products }));
    } catch { /* quota exceeded, ignore */ }
}

async function fetchWithRetry(maxAttempts = 3) {
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const ps = await fetchRestrictedProducts();
            if (Array.isArray(ps) && ps.length > 0) return ps;
            lastErr = new Error("empty product list");
        } catch (err) {
            lastErr = err;
            const status = err?.response?.status;
            if (status === 401 || status === 403) throw err; // auth issues bubble up
        }
        if (attempt < maxAttempts) {
            await new Promise((r) => setTimeout(r, 400 * Math.pow(2, attempt - 1)));
        }
    }
    throw lastErr || new Error("fetch failed");
}

/**
 * Returns { products, source } where source ∈ {memory|session|network|fallback}.
 * Throws only on auth errors (401/403) so the caller can redirect to login.
 */
export async function loadRestrictedCatalog({ force = false } = {}) {
    if (!force && memCache) return { products: memCache, source: "memory" };
    if (!force) {
        const cached = readSession();
        if (cached) { memCache = cached; return { products: cached, source: "session" }; }
    }
    if (!getRestrictedToken()) {
        // No session — return fallback catalog so the UI never breaks for testers.
        return { products: FALLBACK_PRODUCTS, source: "fallback" };
    }
    if (!inflight) {
        inflight = (async () => {
            try {
                const ps = await fetchWithRetry();
                memCache = ps; writeSession(ps);
                return { products: ps, source: "network" };
            } finally { inflight = null; }
        })();
    }
    try {
        return await inflight;
    } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) throw err;
        // Hard network error / CORS / 5xx — show fallback so the customer
        // ALWAYS sees the catalog. They can still add to cart and check out.
        memCache = FALLBACK_PRODUCTS;
        return { products: FALLBACK_PRODUCTS, source: "fallback" };
    }
}

export async function findRestrictedProduct(slug) {
    const { products, source } = await loadRestrictedCatalog();
    const found = products.find((p) => p.slug === slug || p.id === slug);
    if (found) return { product: found, source };
    // Slug not in cache, force a refresh in case the catalog was updated.
    const { products: fresh, source: src2 } = await loadRestrictedCatalog({ force: true });
    const f2 = fresh.find((p) => p.slug === slug || p.id === slug);
    if (f2) return { product: f2, source: src2 };
    // Last resort, check the hardcoded fallback (handles cold deep-links).
    const fb = FALLBACK_PRODUCTS.find((p) => p.slug === slug || p.id === slug);
    if (fb) return { product: fb, source: "fallback" };
    return { product: null, source: src2 };
}

export function clearRestrictedCatalogCache() {
    memCache = null;
    try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

export { FALLBACK_PRODUCTS };
